import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { resolveImage } from "@/lib/api";

export default function Gallery({ images, name }) {
  const [active, setActive] = useState(null);
  if (!images?.length) return null;
  const prev = () => setActive((i) => (i - 1 + images.length) % images.length);
  const next = () => setActive((i) => (i + 1) % images.length);

  return (
    <section className="bg-navy px-6 py-20 lg:px-12 lg:py-28" data-testid="yacht-gallery">
      <div className="mx-auto max-w-7xl">
        <p className="mb-8 text-[10px] uppercase tracking-[0.45em] text-gold">Gallery — {images.length} images</p>
        <div className="grid gap-4 md:grid-cols-3">
          {images.map((src, i) => (
            <motion.button
              key={`${src}-${i}`}
              type="button"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.08 }}
              onClick={() => setActive(i)}
              className={`group relative overflow-hidden border border-navy-ink/10 ${i === 0 ? "aspect-[21/9] md:col-span-3" : "aspect-[4/3]"}`}
              data-testid={`yacht-gallery-item-${i}`}
            >
              <img src={resolveImage(src)} alt={`${name} ${i + 1}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
              <span className="absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.35em] text-white/80 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                View {String(i + 1).padStart(2, "0")}
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      <Dialog open={active !== null} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-[96vw] rounded-none border-0 bg-black/95 p-0 sm:max-w-6xl [&>button]:hidden" data-testid="yacht-lightbox">
          <DialogTitle className="sr-only">{name} gallery</DialogTitle>
          {active !== null && (
            <div className="relative">
              <img src={resolveImage(images[active])} alt={`${name} ${active + 1}`} className="max-h-[85vh] w-full object-contain" data-testid="yacht-lightbox-image" />
              <button onClick={() => setActive(null)} className="absolute right-4 top-4 rounded-full bg-black/50 p-2 text-white hover:text-gold-pale" aria-label="Close" data-testid="yacht-lightbox-close"><X size={18} /></button>
              {images.length > 1 && (
                <>
                  <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-3 text-white hover:text-gold-pale" aria-label="Previous" data-testid="yacht-lightbox-prev"><ChevronLeft size={20} /></button>
                  <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-3 text-white hover:text-gold-pale" aria-label="Next" data-testid="yacht-lightbox-next"><ChevronRight size={20} /></button>
                </>
              )}
              <span className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.35em] text-white/70" data-testid="yacht-lightbox-counter">
                {active + 1} / {images.length}
              </span>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
